/**
 * Excel number formatting helper functions
 */

export interface LocaleInfo {
    locale: string;
    thousandsSeparator: string;
    decimalSeparator: string;
    largeNumberThreshold: number;
    numberPattern: string;
}

/**
 * Gets the locale-specific currency symbol
 */
export const getLocaleCurrencySymbol = (): string => {
    try {
        // Create a NumberFormat object with currency style for the user's default locale
        const formatter = new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: 'USD', // Placeholder currency, the symbol will be locale-dependent
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        });

        // Format a dummy number (e.g., 0) and extract the symbol
        const parts = formatter.formatToParts(0);
        const currencyPart = parts.find(part => part.type === 'currency');
        return currencyPart ? currencyPart.value : '';
    } catch (e) {
        console.warn("Could not determine locale currency symbol using Intl.NumberFormat:", e);
        return '';
    }
};

/**
 * Gets locale information from currency symbol
 */
export const getLocaleInfoFromCurrencySymbol = (currencySymbol: string): LocaleInfo => {
    // Currency symbol to locale mapping (no hardcoded patterns)
    const currencyLocaleMap: { [key: string]: string } = {
        '$': 'en-US',
        'US$': 'en-US',
        'CA$': 'en-CA',
        'A$': 'en-AU',
        'NZ$': 'en-NZ',
        'HK$': 'en-HK',
        'MX$': 'es-MX',
        'R$': 'pt-BR',
        '€': 'de-DE',
        '£': 'en-GB',
        '¥': 'ja-JP',
        'CN¥': 'zh-CN',
        '₹': 'hi-IN',
        '₩': 'ko-KR',
        '₱': 'fil-PH',
        '₫': 'vi-VN',
        'CHF': 'de-CH',
        'SEK': 'sv-SE',
        'NOK': 'nb-NO',
        'DKK': 'da-DK',
        'PLN': 'pl-PL',
        'CZK': 'cs-CZ',
        'HUF': 'hu-HU',
        'RON': 'ro-RO',
        'BGN': 'bg-BG',
        'TRY': 'tr-TR',
        'ZAR': 'en-ZA',
        'THB': 'th-TH',
        'SGD': 'en-SG',
        'MYR': 'ms-MY',
        'IDR': 'id-ID',
        'AED': 'ar-AE',
        'SAR': 'ar-SA',
        'EGP': 'ar-EG',
        'UAH': 'uk-UA',
        'RSD': 'sr-RS'
    };

    const locale = currencyLocaleMap[currencySymbol] || 'en-US';

    try {
        // Test with different numbers to understand the locale's number formatting pattern
        const formatter = new Intl.NumberFormat(locale);

        // Test numbers to analyze patterns
        const testNumbers = [1234567, 12345, 1234, 123];
        const formattedNumbers = testNumbers.map(num => formatter.format(num));

        let thousandsSeparator = ',';
        let decimalSeparator = '.';
        let numberPattern = '#,##0';

        // Analyze the formatted numbers to extract patterns
        for (const formatted of formattedNumbers) {
            // Find thousands separator by looking for non-digit characters that repeat
            const separators = formatted.match(/\D/g) || [];
            if (separators.length > 0) {
                // The most common separator is likely the thousands separator
                const separatorCounts: { [key: string]: number } = {};
                for (const sep of separators) {
                    separatorCounts[sep] = (separatorCounts[sep] || 0) + 1;
                }

                const separatorKeys = Object.keys(separatorCounts);
                if (separatorKeys.length > 0) {
                    const mostCommonSeparator = separatorKeys
                        .reduce((a, b) => separatorCounts[a] > separatorCounts[b] ? a : b);

                    if (mostCommonSeparator && mostCommonSeparator !== '.' && mostCommonSeparator !== ',') {
                        thousandsSeparator = mostCommonSeparator;
                    } else if (formatted.includes(',') && !formatted.endsWith(',')) {
                        thousandsSeparator = ',';
                    } else if (formatted.includes('.') && formatted.indexOf('.') < formatted.length - 3) {
                        thousandsSeparator = '.';
                    } else if (formatted.includes(' ')) {
                        thousandsSeparator = ' ';
                    }
                }
            }
        }

        // Test decimal formatting
        const decimalTest = formatter.format(1234.56);
        const decimalMatch = decimalTest.match(/(\D)(\d{1,2})$/);
        if (decimalMatch) {
            decimalSeparator = decimalMatch[1];
        }

        // Generate dynamic pattern based on actual formatting
        numberPattern = generateNumberPattern(formattedNumbers, thousandsSeparator);

        // Different locales have different thresholds for using thousands separators
        const largeNumberThreshold = determineThreshold(formattedNumbers, thousandsSeparator);

        return {
            locale,
            thousandsSeparator,
            decimalSeparator,
            largeNumberThreshold,
            numberPattern
        };
    } catch (e) {
        // Fallback to US format
        return {
            locale: 'en-US',
            thousandsSeparator: ',',
            decimalSeparator: '.',
            largeNumberThreshold: 1000,
            numberPattern: '#,##0'
        };
    }
};

/**
 * Generates number pattern based on formatted numbers
 */
export const generateNumberPattern = (formattedNumbers: string[], thousandsSeparator: string): string => {
    // Analyze the longest formatted number to understand the pattern
    const longestNumber = formattedNumbers.reduce((a, b) => a.length > b.length ? a : b);

    // Remove any non-digit, non-separator characters for analysis
    const cleanNumber = longestNumber.replace(/[^\d\s.,'-]/g, '');

    // Count separator occurrences and positions
    const separatorPositions: number[] = [];
    for (let i = 0; i < cleanNumber.length; i++) {
        if (cleanNumber[i] === thousandsSeparator) {
            separatorPositions.push(i);
        }
    }

    if (separatorPositions.length === 0) {
        return '#,##0'; // Return standard pattern instead of just '0'
    }

    // Handle special cases like Indian numbering (12,34,567)
    if (separatorPositions.length > 1) {
        // Check if it's Indian style numbering
        const segments = cleanNumber.split(thousandsSeparator);
        if (segments.length > 2) {
            const lastSegmentLength = segments[segments.length - 1].length;
            const secondLastSegmentLength = segments[segments.length - 2].length;

            if (lastSegmentLength === 3 && secondLastSegmentLength === 2) {
                // Indian numbering system
                return `#${thousandsSeparator}##${thousandsSeparator}##0`;
            }
        }
    }

    // Create a standard pattern based on the thousands separator
    // The key fix: Always use # for optional digits, only use 0 for required digits
    const segments = cleanNumber.split(thousandsSeparator);

    if (segments.length >= 2) {
        // Standard pattern: #,##0 (where # represents optional digits, 0 represents required digits)
        return `#${thousandsSeparator}##0`;
    }

    // Fallback to standard comma-separated format
    return '#,##0';
};

/**
 * Determines the threshold for using thousands separators
 */
export const determineThreshold = (formattedNumbers: string[], thousandsSeparator: string): number => {
    // Find the smallest number that has a thousands separator
    const numbersWithSeparators = formattedNumbers.filter(formatted =>
        formatted.includes(thousandsSeparator)
    );

    if (numbersWithSeparators.length === 0) {
        return 1000; // Default threshold
    }

    // Extract numeric values and find the minimum
    const numericValues = numbersWithSeparators.map(formatted => {
        const cleanNumber = formatted.replace(new RegExp(`\\${thousandsSeparator}`, 'g'), '');
        return parseInt(cleanNumber.replace(/\D/g, ''), 10);
    });

    const minWithSeparator = Math.min(...numericValues);

    // Common thresholds: 1000, 10000
    if (minWithSeparator >= 10000) {
        return 10000;
    } else if (minWithSeparator >= 1000) {
        return 1000;
    } else {
        return 1000; // Default
    }
};

/**
 * Infers a suitable Excel number format string from a given numeric or currency string.
 * This function attempts to detect currency symbols, thousands separators, and decimal places.
 *
 * @param inputString The string representation of a number or currency (e.g., "1,234,567", "$1,234,567.89", "500", "Mex$100").
 * @returns An Excel number format string (e.g., "$#,##0.00", "#,##0", "0.00") or "General" if no specific format can be inferred.
 */
export const inferExcelNumberFormat = (inputString: string): string => {
    if (!inputString || typeof inputString !== 'string') {
        return "General"; // Return general for invalid input
    }

    // Trim whitespace
    let cleanedString = inputString.trim();

    // Prioritize longer symbols first to correctly match "Mex$" before "$"
    const commonCurrencySymbols = ["F  CFA", "AED", "ARS", "AZN", "BGN", "BYR", "CA$",
        "CHF", "CLP", "CN¥", "COP", "CZK", "DKK", "EGP", "GHS", "HK$", "HUF", "IDR",
        "KZT", "MX$", "MYR", "NGN", "NOK", "NZ$", "PEN", "PLN", "PYG", "RON", "RSD",
        "SAR", "SEK", "SGD", "THB", "TRY", "NT$", "UAH", "UYU", "ZAR", "A$", "R$",
        "€", "£", "₹", "¥", "₩", "₱", "$", "₫"].sort((a, b) => b.length - a.length);

    // Add the user's locale currency symbol if available and not already in the list
    const localeCurrencySymbol = getLocaleCurrencySymbol();
    if (localeCurrencySymbol && !commonCurrencySymbols.includes(localeCurrencySymbol)) {
        commonCurrencySymbols.unshift(localeCurrencySymbol);
    }

    let isCurrency = false;
    let currencySymbol = '';
    let symbolIsPrefix = true;
    let localeInfo: LocaleInfo = {
        locale: 'en-US',
        thousandsSeparator: ',',
        decimalSeparator: '.',
        largeNumberThreshold: 1000,
        numberPattern: '#,##0'
    };

    // Handle parentheses for negative numbers FIRST (before currency detection)
    let isNegativeParentheses = false;
    if (cleanedString.startsWith('(') && cleanedString.endsWith(')')) {
        isNegativeParentheses = true;
        cleanedString = cleanedString.substring(1, cleanedString.length - 1).trim();
    }

    // Check for explicit negative sign FIRST
    let hasNegativeSign = cleanedString.startsWith('-');
    if (hasNegativeSign) {
        cleanedString = cleanedString.substring(1).trim();
    }

    // Detect currency symbol AFTER negative processing
    for (const symbol of commonCurrencySymbols) {
        if (cleanedString.startsWith(symbol)) {
            isCurrency = true;
            currencySymbol = symbol;
            cleanedString = cleanedString.substring(symbol.length).trim();
            symbolIsPrefix = true;
            localeInfo = getLocaleInfoFromCurrencySymbol(symbol);
            break;
        }
        if (cleanedString.endsWith(symbol)) {
            const numberPart = cleanedString.substring(0, cleanedString.length - symbol.length).trim();
            // Verify it's actually a number without the symbol
            if (numberPart && !isNaN(parseFloat(numberPart.replace(/[^\d.-]/g, '')))) {
                isCurrency = true;
                currencySymbol = symbol;
                cleanedString = numberPart;
                symbolIsPrefix = false;
                localeInfo = getLocaleInfoFromCurrencySymbol(symbol);
                break;
            }
        }
    }

    // Remove thousands separators based on locale
    cleanedString = cleanedString.replace(new RegExp(`\\${localeInfo.thousandsSeparator}`, 'g'), '');

    // Determine decimal places using locale-specific decimal separator
    const decimalIndex = cleanedString.indexOf(localeInfo.decimalSeparator);
    let decimalPlaces = 0;
    let hasDecimal = false;

    if (decimalIndex !== -1) {
        hasDecimal = true;
        decimalPlaces = cleanedString.length - 1 - decimalIndex;
    }

    // Convert decimal separator for parsing if needed
    const normalizedString = cleanedString.replace(localeInfo.decimalSeparator, '.');
    const numericValue = parseFloat(normalizedString);

    // Construct the format string using dynamically generated pattern
    let format = '';
    const originalHasThousandsSeparator = inputString.includes(localeInfo.thousandsSeparator);

    // Use locale-specific threshold and dynamically generated pattern
    if (originalHasThousandsSeparator || (Math.abs(numericValue) >= localeInfo.largeNumberThreshold)) {
        // Use the dynamically generated number pattern
        format = localeInfo.numberPattern;
    } else {
        format = '0';
    }

    // Add decimal places
    if (hasDecimal) {
        format += '.' + '0'.repeat(decimalPlaces);
    }

    // Add currency symbol
    if (isCurrency) {
        if (symbolIsPrefix) {
            format = currencySymbol + format;
        } else {
            format = format + currencySymbol;
        }
    }

    // Handle negative formatting
    if (isNegativeParentheses || hasNegativeSign) {
        let positiveFormat = format;
        let negativeFormat = '';

        if (isNegativeParentheses) {
            // Format: $#,##0.00_);($#,##0.00)
            if (isCurrency && symbolIsPrefix) {
                const numberPart = format.substring(currencySymbol.length);
                negativeFormat = `${currencySymbol}(${numberPart})`;
            } else if (isCurrency && !symbolIsPrefix) {
                const numberPart = format.substring(0, format.length - currencySymbol.length);
                negativeFormat = `(${numberPart})${currencySymbol}`;
            } else {
                negativeFormat = `(${format})`;
            }
            format = `${positiveFormat}_);${negativeFormat}`;
        } else if (hasNegativeSign) {
            // Format: $#,##0.00;-$#,##0.00
            if (isCurrency && symbolIsPrefix) {
                const numberPart = format.substring(currencySymbol.length);
                negativeFormat = `-${currencySymbol}${numberPart}`;
            } else if (isCurrency && !symbolIsPrefix) {
                const numberPart = format.substring(0, format.length - currencySymbol.length);
                negativeFormat = `-${numberPart}${currencySymbol}`;
            } else {
                negativeFormat = `-${format}`;
            }
            format = `${positiveFormat};${negativeFormat}`;
        }
    }

    // Dynamic threshold based on locale (only for non-currency)
    if (!originalHasThousandsSeparator && Math.abs(numericValue) < localeInfo.largeNumberThreshold && !isCurrency) {
        if (hasDecimal) {
            format = '0.' + '0'.repeat(decimalPlaces);
        } else {
            format = '0';
        }
    }

    // Final validation
    if (format === '' || (format === localeInfo.numberPattern && !isCurrency) || (format === '0' && !isCurrency)) {
        if (hasDecimal) {
            format = (format === '' ? '0' : format) + '.' + '0'.repeat(decimalPlaces);
        } else if (format === '') {
            format = '0';
        }
    }

    return format;
};